import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { SafetyEvent } from '@apis/reports';
import { DataRow } from '../../shared/DataRow/DataRow';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { APP_DEFAULT_TZ, formatTime } from '@utils/dateUtils';
import classes from './SafetyEventsSection.module.css';

interface ISafetyEventsSectionProps {
  events: SafetyEvent[];
}

/**
 * Per-stop safety events list. One row per event with:
 *   - severity dot (red for critical, amber otherwise) as the leadingIcon
 *   - violation-type label (i18n-translated when a key exists under
 *     `safety.violations.${type}`, raw string otherwise) preceded by the
 *     event's HH:MM
 *   - trade chip on the right, using the trade's backgroundColor
 */
export function SafetyEventsSection({ events }: ISafetyEventsSectionProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'ja' ? 'ja' : 'en';
  const tz = useSelector(selectSiteTz) ?? APP_DEFAULT_TZ;

  if (events.length === 0) {
    return (
      <div role="status" className={classes.empty}>
        {t('reports.incidentReport.empty.safetyEvents')}
      </div>
    );
  }

  return (
    <ul className={classes.list}>
      {events.map((event) => {
        const dotClass = event.isCritical
          ? `${classes.dot} ${classes.dotCritical}`
          : classes.dot;
        const label = t(`safety.violations.${event.safetyViolationType}`, {
          defaultValue: event.safetyViolationType,
        });
        const timeLabel = formatTime(new Date(event.timestamp), lang, tz);
        const chip = event.trade
          ? {
              text: event.trade.name,
              ...(event.trade.backgroundColor
                ? { backgroundColor: event.trade.backgroundColor }
                : {}),
            }
          : undefined;
        return (
          <DataRow
            key={event.safetyEventId}
            label={`${timeLabel} · ${label}`}
            leadingIcon={<span className={dotClass} aria-hidden="true" />}
            {...(chip ? { chip } : {})}
          />
        );
      })}
    </ul>
  );
}
